import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { useDebounce } from 'use-debounce';
// api
import moment from 'moment';
import { useScrollCacheHook } from '@ontruck/fe-ui';
import getFilterViews from 'modules/emergencies/api/getFilterViews';
import { SETTINGS } from 'modules/emergencies/constants';

// calls api to get availableFilterViews
const availableFilterViewsFetcher = async () => {
  return getFilterViews();
};

const getInitialCreatedAtAfterDate = () => {
  const today = moment();
  const initialDate = today.subtract(7, 'days');
  const formatedInitialDate = initialDate.format('YYYY-MM-DD');
  return formatedInitialDate;
};

const readInternalSelectedFilters = () => {
  const internalSelectedFiltersString = localStorage.getItem(SETTINGS.FILTERS_LOCAL_STORAGE_KEY);
  try {
    const internalSelectedFilters = JSON.parse(internalSelectedFiltersString);
    return internalSelectedFilters;
  } catch (e) {
    return null;
  }
};

// returns available with loading/reloading state
// returns selectedFilters and functions to update them
const useTicketsFilters = () => {
  const { resetScrollCache } = useScrollCacheHook('tickets-list');
  const initialCreatedAtAfterDate = getInitialCreatedAtAfterDate();
  const defaultSelectedFilters = {
    filtersView: -1,
    search: '',
    state: [],
    origin_country_group: '',
    destination_country_group: '',
    created_at_after: initialCreatedAtAfterDate,
    assigned_to: [],
  };

  const initialSelectedFiltersFromStorage = readInternalSelectedFilters();

  const initialSelectedFilters = initialSelectedFiltersFromStorage || defaultSelectedFilters;

  const [internalSelectedFilters, setInternalSelectedFilters] = useState(initialSelectedFilters);
  const [selectedFilters] = useDebounce(internalSelectedFilters, 500);

  const availableFilterViewsRequestKey = 'filter-views';

  const { data: availableFilterViews, error: availableFilterViewsError } = useSWR(
    availableFilterViewsRequestKey,
    availableFilterViewsFetcher
  );

  const isLoadingAvailableFilterViews = typeof availableFilterViews === 'undefined';

  // save filters to local storage
  useEffect(() => {
    const selectedFiltersSTRING = JSON.stringify(selectedFilters);
    localStorage.setItem(SETTINGS.FILTERS_LOCAL_STORAGE_KEY, selectedFiltersSTRING);
    resetScrollCache();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ^^
  }, [selectedFilters]);

  const setFiltersView = (value) => {
    if (value === -1) {
      setInternalSelectedFilters({
        ...defaultSelectedFilters,
        filtersView: value,
      });
    } else {
      const filterViewFilters = availableFilterViews.results.find(
        (filterView) => filterView.id === value
      ).filters;
      const filterViewFiltersJson = JSON.parse(filterViewFilters);
      const filtersFromFilterView = {
        ...internalSelectedFilters,
        filtersView: value,
        ...filterViewFiltersJson,
      };
      setInternalSelectedFilters(filtersFromFilterView);
    }
  };

  const setSearch = (value) => {
    setInternalSelectedFilters({
      ...internalSelectedFilters,
      search: value,
    });
  };

  const setCreatedAtAfter = (value) => {
    setInternalSelectedFilters({
      ...internalSelectedFilters,
      created_at_after: value,
    });
  };

  const setState = (value) => {
    setInternalSelectedFilters({
      ...internalSelectedFilters,
      state: value,
    });
  };

  const setOriginCountryGroup = (value) => {
    setInternalSelectedFilters({
      ...internalSelectedFilters,
      origin_country_group: value,
    });
  };

  const setDestinationCountryGroup = (value) => {
    setInternalSelectedFilters({
      ...internalSelectedFilters,
      destination_country_group: value,
    });
  };

  const defaultFiltersViewOption = {
    value: -1,
    label: 'Default view',
  };
  const savedFilterViewsOptions = useMemo(() => {
    if (isLoadingAvailableFilterViews) return [];
    if (availableFilterViewsError) return [];
    try {
      return availableFilterViews.results.map((filterView) => ({
        value: filterView.id,
        label: filterView.name,
      }));
    } catch (e) {
      return [];
    }
  }, [availableFilterViews, availableFilterViewsError, isLoadingAvailableFilterViews]);

  const availableFiterViewsOptions = [defaultFiltersViewOption, ...savedFilterViewsOptions];

  return {
    selectedFilters,
    internalSelectedFilters,
    isLoadingAvailableFilterViews,
    availableFilterViews,
    availableFilterViewsError,
    setFiltersView,
    setSearch,
    setCreatedAtAfter,
    setState,
    setOriginCountryGroup,
    setDestinationCountryGroup,
    availableFiterViewsOptions,
  };
};

export default useTicketsFilters;
